import React, { useEffect, useState } from 'react';

import { Form } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { createIncentive, getCommunityIncentiveNotificationRecipients, updateIncentive } from 'apis/CommunityAPI';
import Button from 'components/lib/Button';
import Checkbox from 'components/lib/Checkbox';
import DatePicker from 'components/lib/DatePicker';
import Modal from 'components/lib/Modal';
import Select from 'components/lib/Select';
import TextArea from 'components/lib/TextArea';
import Upload from 'components/lib/Upload';
import { OWNERSHIP_TYPES } from 'constants/ownership';
import { useAppSelector } from 'store/hook';
import { CommunityType } from 'types/communities';
import { CommunityIncentiveType } from 'types/incentives';
import { convertToBase64, fetchFiles, getFileExtension, normalizeUploadFileListEvent } from 'utils/helpers/files';

import CommunityIncentiveNotificationRecipients from './CommunityIncentiveNotificationRecipients';
import { useCommunityIncentivesContext } from './CommunityIncentivesContext';
import {
  INCENTIVE_MODAL_LABELS,
  INCENTIVE_MODAL_TYPE,
  INCENTIVE_SOURCE_ALL_OPTIONS,
  INCENTIVE_SOURCE_SHOP_REQUEST_OPTIONS,
} from './constants';
import { FILE_TYPE_EXTENSIONS } from '../../../constants';
import { useRootContext } from '../../layout/RootContext';
import CommunityInfoConfirmationModal from '../CommunityInfoConfirmationModal';

const StyledModal = styled(Modal)`
  &&& .ant-modal {
    max-width: 1040px;
  }
  .ant-modal-close {
    top: 22px;
  }
  &&& .ant-modal-body {
    padding: 25px 0;
  }
  &&& .ant-modal-footer {
    margin-top: 0;
    button {
      font-size: 14px;
    }
  }
  .ant-modal-content {
    max-width: 1040px;
    .ant-modal-header {
      margin-bottom: 0;
    }
    .ant-modal-footer {
      display: flex;
      flex-flow: row-reverse;
      button {
        margin-right: 10px;
        border-radius: 4px;
        width: 155px;
      }
    }
  }
`;

const Label = styled.div`
  font-family: var(--font-bold);
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  gap: 20px;
  padding: 0 30px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Footer = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  padding-right: 30px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid var(--line-gray);
`;

type Props = {
  openModal: boolean;
  incentive?: CommunityIncentiveType;
  modalType: string | undefined;
  ownershipType: number;
  community?: CommunityType;
  disabled?: boolean;
  shopRequestId?: number;
  onClose: () => void;
};

const CommunityIncentivesModal = ({
  openModal,
  incentive,
  modalType,
  ownershipType,
  community,
  disabled = false,
  shopRequestId,
  onClose,
}: Props) => {
  const livingTypes = useAppSelector((state) => state.global.livingTypes);
  const { selectedParentCommunity } = useAppSelector((state) => state.global);

  const { showMessage } = useRootContext();
  const { refetch } = useCommunityIncentivesContext();
  const [loading, setLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = React.useState<boolean>(false);
  const [payload, setPayload] = useState<any>(null);
  const [notificationRecipients, setNotificationRecipients] = useState<any[]>([]);

  const [form] = Form.useForm();

  const showIncentiveSource = shopRequestId != null || ownershipType === OWNERSHIP_TYPES.SHARED;
  const incentiveSourceOptions =
    shopRequestId != null ? INCENTIVE_SOURCE_SHOP_REQUEST_OPTIONS : INCENTIVE_SOURCE_ALL_OPTIONS;

  useEffect(() => {
    const getInitialData = async () => {
      if (incentive) {
        const currentFiles = incentive.files || [];
        const currentImages = incentive.images || [];
        const filesPayload = [...currentFiles, ...currentImages].map((file: any) => ({
          ...file,
          file_name: `${file.file_name}.${file.file_extension}`,
          file_path: file.path,
        }));

        const files = await fetchFiles(filesPayload);
        form.setFieldsValue({
          description: incentive.description,
          date: dayjs(incentive.date).hour(1),
          living_types: incentive.living_types,
          files_upload: files,
          incentive_source: incentive.incentive_source,
        });
      } else {
        form.resetFields();
        if (community?.id) {
          try {
            const data = await getCommunityIncentiveNotificationRecipients(community.id);
            setNotificationRecipients(data ?? []);
          } catch (error) {
            console.error('Failed to fetch incentive recipients', error);
          }
        }
      }
    };

    getInitialData();
  }, [openModal, modalType, form, incentive, community?.id]);

  const onSubmit = async (values: CommunityIncentiveType) => {
    setLoading(true);
    const { date, files_upload } = values;

    const alFilesPayload = await Promise.all(
      (files_upload ?? []).map((file: any) => convertToBase64(file.originFileObj))
    );

    const allFilesDataPayload = (alFilesPayload ?? []).map((item, index) => ({
      id: files_upload?.length ? files_upload[index].id : undefined,
      name: files_upload?.length ? (files_upload[index].name ?? 'Untitled') : 'Untitled',
      mock: files_upload?.length ? files_upload[index].mock : undefined,
      extension: files_upload?.length ? files_upload[index].extension : undefined,
      file: {
        mock: true,
        name: files_upload?.length ? (files_upload[index].name ?? 'Untitled') : 'Untitled',
        data: item,
      },
    }));

    const filesDataPayload = allFilesDataPayload?.filter(
      (file: any) => !FILE_TYPE_EXTENSIONS.IMAGES.includes((file.extension ?? '').toLowerCase())
    );
    const imagesDataPayload = allFilesDataPayload?.filter((file: any) =>
      FILE_TYPE_EXTENSIONS.IMAGES.includes((file.extension ?? '').toLowerCase())
    );

    const request = {
      ...values,
      company_id: selectedParentCommunity ? selectedParentCommunity.company_id : undefined,
      shared: ownershipType == OWNERSHIP_TYPES.SHARED,
      date: dayjs(date).hour(12),
      files_upload: filesDataPayload,
      images_upload: imagesDataPayload,
      community: community?.id,
      shop_request: shopRequestId ?? undefined,
    };

    // If shared, show confirmation modal
    // If private, submit directly
    if (ownershipType == OWNERSHIP_TYPES.SHARED) {
      setPayload(request);
      setShowConfirmationModal(true);
    } else if (ownershipType == OWNERSHIP_TYPES.PRIVATE) {
      handleIncentive(request).then((response) => {
        setPayload(null);
        form.resetFields();
      });
    }
  };

  const handleIncentive = async (payload: any) => {
    setLoading(true);
    if (modalType === INCENTIVE_MODAL_TYPE.EDIT && incentive) {
      await updateIncentive(community?.id ?? -1, incentive.id as number, payload)
        .then(() => {
          refetch();
          handleCancel();
          showMessage('success', `Community Incentive edited successfully`);
        })
        .catch((reason: any) => {
          console.error(reason);
          showMessage('error', `Failed to edit Community Incentive`);
        });
    } else {
      await createIncentive(community?.id ?? -1, payload)
        .then(() => {
          refetch();
          handleCancel();
          showMessage('success', `Community Incentive added successfully`);
        })
        .catch((reason: any) => {
          console.error(reason);
          showMessage('error', `Failed to add Community Incentive`);
        });
    }
    setLoading(false);
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
  };

  const handleCustomRequest = (options: any) => {
    // Set the initial status to "uploading"
    const { file, onSuccess } = options;
    // Set the status to "uploading"
    setLoading(true);
    file.status = 'uploading';
    file.extension = getFileExtension(file.name);

    setTimeout(() => {
      // Mark as "done"
      file.status = 'done';
      onSuccess('done');
      setLoading(false);
    }, 1500);
  };

  return (
    <>
      <StyledModal
        open={openModal}
        onCancel={handleCancel}
        footer={null}
        maskClosable={false}
        title={
          <div>
            <div>{INCENTIVE_MODAL_LABELS[modalType ?? ''].title}</div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              for <span style={{ color: 'var(--dark-blue)' }}>{community?.comunity_name}</span>
            </div>
          </div>
        }
        width="fit-content"
      >
        <Form
          form={form}
          layout="vertical"
          disabled={disabled}
          initialValues={{
            date: dayjs().hour(12).minute(0),
          }}
          onFinish={(values) => {
            onSubmit(values);
          }}
        >
          <Row>
            <Column>
              <Label>Incentive Description</Label>
              <Form.Item name="description" rules={[{ required: true, message: 'Please enter a description' }]}>
                <TextArea
                  style={{ width: 500, resize: 'vertical', minHeight: 128, maxHeight: 320 }}
                  rows={4}
                  placeholder="Enter a description"
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const currentFiles = getFieldValue('files_upload');
                  return (
                    <Form.Item
                      name="files_upload"
                      valuePropName="files"
                      getValueFromEvent={normalizeUploadFileListEvent}
                    >
                      <Upload multiple customRequest={handleCustomRequest} fileList={currentFiles} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Column>
            <Column style={{ gap: 20, width: 230 }}>
              <div>
                <Label>Incentive Date</Label>
                <Form.Item name="date" rules={[{ required: true, message: 'Please select a date' }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </div>
              {showIncentiveSource && (
                <div>
                  <Label>Incentive Source</Label>
                  <Form.Item
                    name="incentive_source"
                    rules={[{ required: true, message: 'Please select an incentive source' }]}
                  >
                    <Select
                      className="rectangular"
                      placeholder="Select incentive source"
                      options={incentiveSourceOptions}
                    />
                  </Form.Item>
                </div>
              )}
              <div>
                <Label>Living Types</Label>
                <Form.Item
                  name="living_types"
                  rules={[{ required: true, message: 'Please select at least one living type' }]}
                >
                  <CheckboxGroup>
                    {livingTypes.map((livingType) => (
                      <Checkbox key={livingType.id} value={livingType.id} className={'green'}>
                        {livingType.type}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                </Form.Item>
              </div>
            </Column>
          </Row>
          {ownershipType === OWNERSHIP_TYPES.SHARED && (
            <CommunityIncentiveNotificationRecipients
              notificationRecipients={notificationRecipients}
            ></CommunityIncentiveNotificationRecipients>
          )}
          <Footer>
            <Form.Item>
              <Button
                loading={loading}
                disabled={loading || disabled}
                type={'primary'}
                htmlType="submit"
                style={{ marginRight: '10px' }}
              >
                {INCENTIVE_MODAL_LABELS[modalType ?? ''].action}
              </Button>
              <Button disabled={false} onClick={handleCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Footer>
        </Form>
      </StyledModal>
      <CommunityInfoConfirmationModal
        showCommunityIncentivesConfirmationModal={showConfirmationModal}
        communityName={community?.comunity_name}
        onConfirm={async () => {
          await handleIncentive(payload);
          setShowConfirmationModal(false);
          setPayload(null);
          form.resetFields();
        }}
        onClose={() => {
          setShowConfirmationModal(false);
          handleCancel();
        }}
      />
    </>
  );
};

export default CommunityIncentivesModal;
